import {
    Component,
    ElementRef,
    HostListener,
    Injector,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { AppComponentBase } from '@shared/common/app-component-base';
import {
    UNIVERSAL_SEARCH_DEBOUNCE_MS,
    UNIVERSAL_SEARCH_MIN_QUERY_LENGTH,
    UniversalSearchGroup,
    UniversalSearchResultItem,
    UniversalSearchUiState,
} from './universal-search.models';
import { UniversalSearchResponse, UniversalSearchService } from './universal-search.service';
import { flattenGroupedResults, resolveEnterNavigationTarget } from './universal-search.utils';
import { AppRoutes } from '@app/shared/routes/app-routes';

@Component({
    standalone: false,
    selector: 'universal-search',
    templateUrl: './universal-search.component.html',
    styleUrls: ['./universal-search.component.scss'],
    providers: [UniversalSearchService],
})
export class UniversalSearchComponent extends AppComponentBase implements OnInit, OnDestroy {
    @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

    readonly resultsId = `universal-search-results-${Math.random().toString(36).slice(2, 9)}`;

    query = '';
    groups: UniversalSearchGroup[] = [];
    uiState: UniversalSearchUiState = 'idle';
    isDropdownOpen = false;
    highlightedIndex: number | null = null;
    isCompactPlaceholder = false;

    private readonly queryChanges$ = new Subject<string>();
    private searchSub: Subscription;
    private latestRequestId = 0;
    private mediaQuery: MediaQueryList;
    private mediaQueryListener: (event: MediaQueryListEvent) => void;

    constructor(
        injector: Injector,
        private _router: Router,
        private _universalSearchService: UniversalSearchService,
        private _host: ElementRef<HTMLElement>,
        private _ngZone: NgZone
    ) {
        super(injector);
        this.searchSub = this.queryChanges$
            .pipe(
                debounceTime(UNIVERSAL_SEARCH_DEBOUNCE_MS),
                distinctUntilChanged(),
                switchMap((value) => {
                    const trimmed = (value ?? '').trim();
                    if (trimmed.length < UNIVERSAL_SEARCH_MIN_QUERY_LENGTH) {
                        this.resetToIdle();
                        return of(null);
                    }

                    const requestId = ++this.latestRequestId;
                    this.groups = [];
                    this.highlightedIndex = null;
                    this.uiState = 'loading';
                    this.isDropdownOpen = true;

                    return this._universalSearchService.search(trimmed).pipe(
                        map((result) => ({ requestId, result }))
                    );
                })
            )
            .subscribe((payload) => {
                if (!payload || payload.requestId !== this.latestRequestId) {
                    return;
                }
                this.applySearchResult(payload.result);
            });
    }

    ngOnInit(): void {
        this.initPlaceholderBreakpoint();
    }

    ngOnDestroy(): void {
        this.searchSub?.unsubscribe();
        this.queryChanges$.complete();
        if (this.mediaQuery && this.mediaQueryListener) {
            if (this.mediaQuery.removeEventListener) {
                this.mediaQuery.removeEventListener('change', this.mediaQueryListener);
            } else {
                (this.mediaQuery as any).removeListener(this.mediaQueryListener);
            }
        }
    }

    get placeholderText(): string {
        return this.isCompactPlaceholder
            ? this.l('UniversalSearchCompactPlaceholder')
            : this.l('UniversalSearchPlaceholder');
    }

    onQueryInput(value: string): void {
        this.query = value;
        this.queryChanges$.next(value);
    }

    onFocus(): void {
        if ((this.query ?? '').trim().length >= UNIVERSAL_SEARCH_MIN_QUERY_LENGTH) {
            this.isDropdownOpen = true;
        }
    }

    onKeyDown(event: KeyboardEvent): void {
        const flat = flattenGroupedResults(this.groups);

        switch (event.key) {
            case 'ArrowDown':
                if (!this.isDropdownOpen && flat.length) {
                    this.isDropdownOpen = true;
                }
                if (!flat.length) {
                    return;
                }
                event.preventDefault();
                this.highlightedIndex =
                    this.highlightedIndex === null
                        ? 0
                        : Math.min(this.highlightedIndex + 1, flat.length - 1);
                break;
            case 'ArrowUp':
                if (!flat.length) {
                    return;
                }
                event.preventDefault();
                this.highlightedIndex =
                    this.highlightedIndex === null
                        ? flat.length - 1
                        : Math.max(this.highlightedIndex - 1, 0);
                break;
            case 'Enter':
                event.preventDefault();
                this.handleEnter();
                break;
            case 'Escape':
                event.preventDefault();
                this.closeDropdown(true);
                break;
            case 'Tab':
                this.closeDropdown(false);
                break;
            default:
                break;
        }
    }

    onResultClick(item: UniversalSearchResultItem): void {
        this.navigateTo(item);
    }

    onResultMouseEnter(flatIndex: number): void {
        this.highlightedIndex = flatIndex;
    }

    clearQuery(event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        this.latestRequestId++;
        this.query = '';
        this.queryChanges$.next('');
        this.resetToIdle();
        this.searchInput?.nativeElement?.focus();
    }

    getFlatIndex(groupIndex: number, itemIndex: number): number {
        let index = 0;
        for (let i = 0; i < groupIndex; i++) {
            index += this.groups[i].items.length;
        }
        return index + itemIndex;
    }

    trackByGroup(_index: number, group: UniversalSearchGroup): string {
        return group.category;
    }

    trackByItem(_index: number, item: UniversalSearchResultItem): string {
        return item.id;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this._host.nativeElement.contains(event.target as Node)) {
            this.closeDropdown(false);
        }
    }

    @HostListener('document:keydown', ['$event'])
    onDocumentKeyDown(event: KeyboardEvent): void {
        if (!event.altKey || event.key.toLowerCase() !== 'k') {
            return;
        }

        const input = this.searchInput?.nativeElement;
        // The component exists in both desktop and mobile headers; only the visible instance responds.
        if (!input || input.offsetParent === null) {
            return;
        }

        event.preventDefault();
        input.focus();
    }

    private handleEnter(): void {
        const target = resolveEnterNavigationTarget(this.groups, this.highlightedIndex);
        if (target) {
            this.navigateTo(target);
            return;
        }

        // Multiple results, none highlighted — keep focus and dropdown open.
        if (this.groups.length && this.isDropdownOpen) {
            this.searchInput?.nativeElement?.focus();
        }
    }

    private navigateTo(item: UniversalSearchResultItem): void {
    if (!item?.route) {
        return;
    }

    if (
        item.category === 'Employees' &&
        item.route === AppRoutes.activitiesTimeAndAttendance &&
        !this.permission.isGranted('Pages.Activity.ReamTimeTracking') &&
        !this.permission.isGranted('Pages.Activity.TimeAndAttendence')
    ) {
        this.notify.error(
            this.l('You do not have permission to access Time and Attendance.')
        );
        return;
    }

    this._router
        .navigate([item.route], {
            state: item.navigationState,
            queryParams: item.queryParams,
        })
        .then(() => {
            this.query = '';
            this.resetToIdle();
        });
}

    private applySearchResult(result: UniversalSearchResponse): void {
        this.groups = result.groups ?? [];
        this.highlightedIndex = null;

        if (result.error && !this.groups.length) {
            this.uiState = 'error';
            this.isDropdownOpen = true;
            return;
        }

        if (result.error) {
            // Keep local matches but show inline error messaging.
            this.uiState = 'error';
            this.isDropdownOpen = true;
            return;
        }

        this.uiState = this.groups.length ? 'results' : 'empty';
        this.isDropdownOpen = true;
    }

    private resetToIdle(): void {
        this.groups = [];
        this.highlightedIndex = null;
        this.uiState = 'idle';
        this.isDropdownOpen = false;
    }

    private closeDropdown(restoreFocus: boolean): void {
        this.isDropdownOpen = false;
        this.highlightedIndex = null;
        if (restoreFocus) {
            this.searchInput?.nativeElement?.focus();
        }
    }

    private initPlaceholderBreakpoint(): void {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return;
        }

        this.mediaQuery = window.matchMedia('(max-width: 575.98px)');
        this.isCompactPlaceholder = this.mediaQuery.matches;
        this.mediaQueryListener = (event: MediaQueryListEvent) => {
            this._ngZone.run(() => {
                this.isCompactPlaceholder = event.matches;
            });
        };

        if (this.mediaQuery.addEventListener) {
            this.mediaQuery.addEventListener('change', this.mediaQueryListener);
        } else {
            (this.mediaQuery as any).addListener(this.mediaQueryListener);
        }
    }
}
